/**
 * validateMerge.ts - Validate whether the current selection can be merged into a single cell.
 *
 * This is a read-only method (no `Table -> Table` transform); its pure parts live in
 * `TableMergingLogic`. The merge itself is performed separately via `mergeRegion`.
 */

import * as TableConstants from 'notebook/tables/constants/TableConstants';
import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import {type FullTableMap} from 'notebook/tables/excess/EvaluationConstants';
import type {TableNoteContextInterface} from 'notebook/tables/store/contexts/TableNoteContext';
import type {TableMutationParams} from 'notebook/tables/store/logic/mutations/mutationParams';
import {
  computeMergeValidation,
  findTablesReferencingMergedChildCells,
  type MergeValidation,
} from 'notebook/tables/store/logic/TableMergingLogic';
import {getTableMapFromNote} from 'notebook/tables/store/selectors/TableSelectors';
import TableUtil from 'notebook/tables/TableUtil';

/**
 * The result of validating a merge of the current selection, including the merge-expanded box that
 * should be passed to `mergeRegion`.
 */
export interface MergeValidationResult extends MergeValidation {
  /** The merge-expanded selection box to merge. */
  box: MultiSelectionBox;

  /**
   * Names of tables in the entry that contain formulas referencing subordinate (child) cells of the
   * merged region. Merging empties those cells, so such references may become incorrect; the caller
   * should warn the user. Empty when no such references exist.
   */
  tablesReferencingMergedChildCells: Array<string>;
}

/**
 * Builds the map of all tables in the entry/document, keyed by name, so merge validation can scan
 * every table's formulas for references into the region being merged.
 *
 * The full note is read from {@link TableNoteContext} (`getNote`), which resolves to the live note
 * for whatever surface is mounted — regular ELN entries (backed by `EntryStore`), worksheets/documents
 * (`DocumentStore`), templates, etc. Falls back to just the table being edited when no note context is
 * available (e.g. isolated tests or harnesses). The edited table is always overlaid from the live
 * model so its formulas reflect unsaved edits.
 */
export function getEntryTableMap(
  tableNoteContext: TableNoteContextInterface | null,
  currentTable: TableConstants.Table
): FullTableMap {
  const currentTableMap: FullTableMap = {[currentTable.name]: currentTable};
  if (tableNoteContext == null) {
    return currentTableMap;
  }
  const note = tableNoteContext.getNote();
  return {...getTableMapFromNote(note), ...currentTableMap};
}

/**
 * Validate whether the current selection can be merged into a single cell.
 *
 * Reads the merge-expanded selection box from the model and reports whether the selection straddles
 * the freeze boundary (merging disallowed) and whether the merge would discard cell values (caller
 * should confirm). Returns `null` when there is no active selection.
 *
 * @param params - The shared mutation dependencies.
 */
export function validateMerge(params: TableMutationParams): MergeValidationResult | null {
  const model = params.getTableModel();
  const {expandedBox} = model.selection;

  // Nothing to validate when there is no active selection.
  if (expandedBox == null) {
    return null;
  }

  const {table} = params.getTableProps();
  const freezeBoundary = TableUtil.getFreezeBoundary(table);
  const validation = computeMergeValidation(table.table, expandedBox, freezeBoundary);

  const tablesReferencingMergedChildCells = findTablesReferencingMergedChildCells(
    getEntryTableMap(params.tableNoteContext, table),
    table.name,
    expandedBox
  );

  return {box: expandedBox, ...validation, tablesReferencingMergedChildCells};
}
